import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { ACTION_DISPLAY_TYPES } from "../../../constants/enums";
import { PAGES } from "../../../constants/localization";
import EnrollmentApi from "../../../api/enrollment";
import { UpdateCurrentPage } from "../../../actions/enrollment";
import { trackEvent } from "../../../services/eventTracker";
import { EVENTS } from "../../../constants/events";
import { formatDateYYYYMMDD } from "../../../services/formatter";
import i18n from "../../../i18n/config";

import ValidationSummary from "../ValidationSummary";
import GenericForm from "../../Common/DynamicFields/GenericForm";
import PageBlock from "../../Layouts/PageBlock";
import Action from "../../Common/Action";
import SecurityQuestions from "../../../containers/SecurityQuestions/SecurityQuestions";
import EnrollmentFlowScreen from "../../Layouts/EnrollmentFlowScreen";

const AccountSecurityForm = ({ setShowSpinner }) => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState([]);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const formData = useSelector(
    (state) => state.form[PAGES.ENROLLMENT.ACCOUNT_SECURITY.FORM_NAME]
  );
  const userData = useSelector((state) => state.enrollment.userData);
  const currentPage = useSelector((state) => state.enrollment.currentPage);

  /**
   * Call getSecurityQuestions when userData has been updated with data from AccountSecurity component
   */
  useEffect(() => {
    if (userData) {
      getSecurityQuestions();
    }
  }, [userData]);

  /**
   * Get security questions
   */
  const getSecurityQuestions = async () => {
    try {
      let response = await EnrollmentApi.GetSecurityQuestions();
      setSecurityQuestions(response.groups);
    } catch (err) {
      setErrors([PAGES.ENROLLMENT.ERROR_RETRY]);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowSpinner(true);
    const { values } = formData;

    const questions = createQuestionsArr(values);

    try {
      const response = await EnrollmentApi.SaveAccountSecurityV2(
        userData.workflowId,
        questions
      );

      trackEvent(EVENTS.ENROLLMENT.SECURITY_QUESTIONS_SUBMITTED, { success: true });
      setShowSpinner(false);
      dispatch(UpdateCurrentPage(response.nextPage));
    } catch (err) {
      setShowSpinner(false);
      trackEvent(EVENTS.ENROLLMENT.SECURITY_QUESTIONS_SUBMITTED, { success: false, failure_reason: err[0].errorMessage || PAGES.ENROLLMENT.ERROR_GENERAL });
      setErrors([err[0].errorMessage || PAGES.ENROLLMENT.ERROR_GENERAL]);
    }
  };

  /**
   * Create array from submitted form data
   * @param questions AccountSecurityForm data
   */
  const createQuestionsArr = (questions) => {
    const dateObj = new Date(questions.Answer3);
    const formattedDate = formatDateYYYYMMDD(dateObj);

    return [
      {
        groupId: 1,
        passwordQuestionId: questions.Question1,
        answer: questions.Answer1,
      },
      {
        groupId: 2,
        passwordQuestionId: questions.Question2,
        answer: questions.Answer2,
      },
      {
        groupId: 3,
        passwordQuestionId: questions.Question3,
        answer: formattedDate,
      },
    ];
  };

  return (
    <EnrollmentFlowScreen
      flowTitle={i18n.t("PAGES.ENROLLMENT.SECURITY_QUESTIONS.TITLE")}
      showBackButton={true}
      customBackButtonFunc={() => dispatch(UpdateCurrentPage(currentPage - 1))}
    >
      <GenericForm
        form={PAGES.ENROLLMENT.ACCOUNT_SECURITY.FORM_NAME}
        onSubmit={handleSubmit}
        onUpdate={(isFormValid) => setIsDisabled(!isFormValid)}
      >
        <GenericForm.Body>
          <PageBlock.IntroText>
            {
              "In order to manage your bank accounts and allocations, you must select and answer the following security questions to create your account."
            }
          </PageBlock.IntroText>
          <ValidationSummary errors={errors} />
          <PageBlock.Body>
            <SecurityQuestions questions={securityQuestions} />
            <Action
              title={i18n.t("PAGES.ENROLLMENT.SECURITY_QUESTIONS.BUTTON_TEXT")}
              displayType={ACTION_DISPLAY_TYPES.PRIMARY}
              type="submit"
              disabled={isDisabled}
            />
          </PageBlock.Body>
        </GenericForm.Body>
      </GenericForm>
    </EnrollmentFlowScreen>
  );
};

AccountSecurityForm.propTypes = {
  /** set showSpinner boolean from AccountSecurity */
  setShowSpinner: PropTypes.func.isRequired,
  /** react-router history */
  history: PropTypes.object.isRequired,
};

AccountSecurityForm.displayName = "AccountSecurityFormComponent";

export default AccountSecurityForm;
